import {
  Box,
  Grid,
  Typography,
  Link,
  useTheme,
  useMediaQuery,
  type TypographyProps,
} from "@mui/material";
import AccordionSection from "../../shared/accordionSection";
import { ReactNode } from "react";
import { OrderDetailsResponse } from "@/app/interfaces/response/OrderDetailsResponse";
import DateHelper from "@/app/helpers/DateHelper";

const TITLE = "Order Details";

const OrderDetailsCard = ({ order }: { order: OrderDetailsResponse }) => {
  const theme = useTheme();
  const labelColor = theme.palette.text.secondary;
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const getTypography = (
    label: string,
    value: ReactNode,
    variant: TypographyProps["variant"] = "subtitle2"
  ): JSX.Element => {
    return (
      <Typography variant={variant} sx={{ mb: 0.5 }}>
        <Box component="span" sx={{ color: labelColor }}>
          {label}
        </Box>{" "}
        <Box component="span" sx={{ fontWeight: 700, wordBreak: "break-word" }}>
          {value ?? ""}
        </Box>
      </Typography>
    );
  };

  const renderContent = () => (
    <Box
      mt={1.5}
      flex="1"
      display="flex"
      flexDirection="column"
      p={isMobile ? 1 : 3}
      pb={5.5}
      borderRadius={2}
      boxShadow={{
        xs: "0 0px 0px rgba(0,0,0,0)",
        md: "0 1px 3px rgba(0,0,0,0.1)",
      }}
      bgcolor="white"
    >
      {!isMobile && (
        <Typography
          variant="subtitle1"
          fontWeight={600}
          color={labelColor}
          mb={2}
        >
          Order Details
        </Typography>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          {getTypography("Job Name: ", order.jobName)}
          {order.isBackordered && (
            <Typography
              fontStyle="italic"
              fontSize="13px"
              color="#000000"
              mb={2}
              textTransform="uppercase"
            >
              Backordered items appear on this order
            </Typography>
          )}
          {getTypography("Order #: ", order.customOrderNumber)}
          {getTypography(
            "Order Created Date: ",
            DateHelper.toDisplayDate(order.createdOn) || ""
          )}
          {getTypography("Order Type: ", order.inforOrderType)}
          {getTypography("Ship To: ", order.shipTo)}
          {order.placedByUserEmail &&
            getTypography("Placed By: ", order.placedByUserEmail)}
        </Grid>

        <Grid item xs={12} md={6}>
          {getTypography("Order Status: ", order.orderStatus)}
          {getTypography(
            "Order Ready Date: ",
            DateHelper.toDisplayDate(order.dateFilled) || ""
          )}
          {getTypography(
            "Delivery or Pickup: ",
            order.pickUpInStore ? "Pickup" : "Delivery"
          )}
          {getTypography(
            "Order Invoice Date: ",
            DateHelper.toDisplayDate(order.invoiceDate) || ""
          )}
          {getTypography("Branch: ", order.warehouse)}
          {getTypography(
            "Branch email: ",
            <Link href={`mailto:${order.contactEmail}`}>
              {order.contactEmail}
            </Link>
          )}
        </Grid>
      </Grid>
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
            Billing Information:
          </Typography>
          {getTypography("", order.billingAddress?.firstName ?? "")}
          {getTypography("", order.billingAddress?.phoneNumber ?? "")}
          {getTypography("", order.billingAddress?.address1 ?? "")}
          {getTypography("", order.billingAddress?.address2 ?? "")}
          {getTypography("", order.billingAddress?.city ?? "")}
          {getTypography("", order.billingAddress?.stateProvinceName ?? "")}
          {getTypography("", order.billingAddress?.county ?? "")}
          {getTypography("", order.billingAddress?.zipPostalCode ?? "")}
        </Grid>
        <Grid item xs={12} md={6}>
          {!order.pickUpInStore && (
            <>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                Delivery Information:
              </Typography>

              {getTypography("", order.shippingAddress?.firstName ?? "")}
              {getTypography("", order.shippingAddress.address1)}
              {getTypography("", order.shippingAddress.address2)}
              {getTypography("", order.shippingAddress.city)}
              {getTypography("", order.shippingAddress.county)}
              {getTypography("", order.shippingAddress.stateProvinceName)}
              {getTypography("", order.shippingAddress.zipPostalCode)}
            </>
          )}
        </Grid>
      </Grid>
    </Box>
  );

  return isMobile ? (
    <AccordionSection defaultExpanded title={TITLE}>{renderContent()}</AccordionSection>
  ) : (
    renderContent()
  );
};

export default OrderDetailsCard;
